import React from 'react';
import { style } from 'typestyle';

import { percent, px } from 'csx';
import { inject, deps, observer, ComponentProps } from '../../../lib/component';
import { MusicLayout } from '../templates/MusicLayout';
import { Loading } from '../../loading/loading';

import { horizontal, wrap } from 'csstips';
import { rebrand } from '../../../theme/color';
import { ArtistLinkList } from '../../../components/artist-link-list';
import { isEmpty } from '../../../components/project-happy/utilities/objects';
import { defaultHeadingStyles } from '../../../components/project-happy/utilities/font';
import { ArtistProfileImage } from '../molecules/ArtistProfileImage';
import { spacing } from '../../../theme/spacing';
import { Link } from '../atoms/Link';
import { fontstackDefault } from '../config';
import { TrackPageContent } from '../organisms/TrackPageContent';
import { mediaDesktop } from '../utilities/mediaQueries';
import { ArtistTopTracks } from '../organisms/ArtistTopTracks';

export interface TrackPageProps extends ComponentProps {}

const styles = {
  loading: style({
    margin: '25vh 0',
  }),
  masthead: style({
    textAlign: 'center',
  }),
  title: style({
    margin: 0,
  }),
  artistName: style(
    defaultHeadingStyles,
    {
      fontSize: px(24),
      margin: `${spacing.XXSMALL} 0 0`,
      color: rebrand.contrast[30].toString(),
      $nest: {
        '& a': {
          color: rebrand.contrast[30].toString(),
        },
        '& a:hover': {
          textDecoration: 'underline',
        },
      },
    },
    mediaDesktop({
      marginTop: spacing.XSMALL,
    })
  ),
  sidebarArtistGrid: style({
    ...horizontal,
    ...wrap,
    width: percent(100),
    gap: spacing.DEFAULT,
    $nest: {
      '& > *': {
        width: `calc(50% - (${spacing.DEFAULT} / 2))`,
      },
      '&> *:only-child': {
        width: percent(100),
      },
    },
  }),
  artistProfileImage: style(
    {
      maxWidth: px(236),
    },
    mediaDesktop({
      margin: '0 auto',
      maxWidth: px(315),
    })
  ),
  sidebarArtistName: style({
    fontFamily: fontstackDefault,
    fontSize: px(16),
    fontWeight: 700,
    margin: `${spacing.XSMALL} 0 0`,
    $nest: {
      '& a': {
        color: rebrand.contrast[50].toString(),
      },
      '& a:hover': {
        textDecoration: 'underline',
      },
    },
  }),
};

export const TrackPage: React.FC<TrackPageProps> = inject(deps)(
  observer(({ model, controller }) => {
    const { track, loading } = model.page.track;
    const { handleClickLink } = controller.ui;

    if (isEmpty(track) || loading) {
      return <Loading className={styles.loading} />;
    }

    const trackHasMultipleArtists = track.artists && track.artists.length > 1;
    const showArtistTopTracks = track.artists && track.artists.length === 1;

    const MastheadContent = (
      <div className={styles.masthead}>
        <h1 className={styles.title}>{track.title}</h1>
        {track.artists && track.artists.length > 0 && (
          <div className={styles.artistName}>
            <ArtistLinkList
              linkClassName={styles.artistName}
              model={{ artists: track.artists }}
              onClickLink={handleClickLink}
            />
          </div>
        )}
      </div>
    );

    const MainContent = <TrackPageContent track={track} />;

    const SidebarContent = (
      <>
        <div>
          {trackHasMultipleArtists && <h3>Artists</h3>}

          <div className={styles.sidebarArtistGrid}>
            {track.artists.map((artist) => (
              <div key={artist.identity}>
                <ArtistProfileImage artist={artist} isLink className={styles.artistProfileImage} />
                {trackHasMultipleArtists && (
                  <h4 className={styles.sidebarArtistName}>
                    <Link href={`/music/artists/${artist.slug}`}>{artist.name}</Link>
                  </h4>
                )}
              </div>
            ))}
          </div>
        </div>

        {showArtistTopTracks && <ArtistTopTracks artist={track.artists[0]} />}
      </>
    );

    return <MusicLayout mastheadContent={MastheadContent} mainContent={MainContent} sidebarContent={SidebarContent} />;
  })
);
